import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import Logo from "@/assets/watw_logo.webp"; // Use webp version for better performance
import { BOOKING_URL } from "@/constants/urls";

interface NavBarProps {
  scrollToSection?: (id: string) => void;
}

const NavBar: React.FC<NavBarProps> = ({ scrollToSection }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLifeStagesOpen, setIsLifeStagesOpen] = useState(false);
  const [isSupportServicesOpen, setIsSupportServicesOpen] = useState(false);
  const [isInformationOpen, setIsInformationOpen] = useState(false);
  const [isCarePackagesOpen, setIsCarePackagesOpen] = useState(false);
  
  // Mobile menu state
  const [mobileLifeStagesOpen, setMobileLifeStagesOpen] = useState(false);
  const [mobileSupportOpen, setMobileSupportOpen] = useState(false);
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
  const [mobileCarePackagesOpen, setMobileCarePackagesOpen] = useState(false);

  // Close mobile menu when screen size changes
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleLifeStages = () => {
    setIsLifeStagesOpen(!isLifeStagesOpen);
  };

  const toggleSupportServices = () => {
    setIsSupportServicesOpen(!isSupportServicesOpen);
  };

  const toggleInformation = () => {
    setIsInformationOpen(!isInformationOpen);
  };

  const toggleCarePackages = () => {
    setIsCarePackagesOpen(!isCarePackagesOpen);
  };

  const isHomePage = () => {
    return window.location.pathname === '/' || window.location.pathname === '';
  };

  const getNavHref = (href: string) => {
    if (href.startsWith('#') && !isHomePage()) {
      return `/${href}`;
    }
    return href;
  };

  const informationItems = [
    { name: "About", href: "#about" },
    { name: "Team", href: "#team" },
    { name: "Services", href: "#services" },
    { name: "Pricing", href: "#pricing" },
    { name: "FAQ", href: "#faq" },
    { name: "Contact", href: "#contact" },
    { name: "Locations", href: "#locations", subItems: [
      { name: "Currimundi Clinic", href: "/currimundi-clinic" },
      { name: "Online Consultations", href: "/online-consultations" }
    ]},
  ];

  const carePackagesItems = [
    { name: "Skin Package - Introductory Special", href: "/care-packages/skin-package" },
    { name: "Fertility Package - Chinese Medicine + Nutrition", href: "/care-packages/fertility-package" },
  ];

  const lifeStagesItems = [
    { name: "Teens", href: "/teens" },
    { name: "Young Adults", href: "/young-adults" },
    { name: "Preconception Care", href: "/preconception" },
    { name: "Pregnancy", href: "/pregnancy" },
    { name: "Birth Preparation", href: "/birth-prep" },
    { name: "Postpartum", href: "/postpartum" },
    { name: "Perimenopause", href: "/perimenopause" },
    { name: "Menopause", href: "/menopause" },
    { name: "Cycle-Free Health", href: "/cycle-free-health" },
  ];

  const supportServicesItems = [
    { name: "Birth Preparation", href: "/birth-prep" },
    { name: "Cosmetic Acupuncture", href: "/cosmetic-acupuncture" },
    { name: "Digestion & Gut Health", href: "/digestion-gut-health" },
    { name: "Emotional Healing", href: "/emotional-healing" },
    { name: "Fertility Support", href: "/fertility-support" },
    { name: "Headache & Migraine Relief", href: "/headache-migraine" },
    { name: "Immune Support & Seasonal Wellness", href: "/immune-support" },
    { name: "IVF Support", href: "/ivf-support" },
    { name: "Miscarriage Support", href: "/miscarriage-support" },
    { name: "Pain & Injury", href: "/pain-injury" },
    { name: "Preconception Care", href: "/preconception" },
    { name: "Skin Health", href: "/skin-health" },
    { name: "Sleep Support", href: "/sleep-support" },
    { name: "Stress & Burnout", href: "/stress-burnout" },
  ];

  const menuItems = [
    { name: "Blog", href: "/blog" },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, href: string) => {
    if (href.startsWith('#') && scrollToSection && isHomePage()) {
      e.preventDefault();
      scrollToSection(href.replace('#', ''));
      setIsMenuOpen(false);
    }
    if (!isHomePage()) {
      setIsMenuOpen(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 shadow-md border-b border-amber-200/30">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between py-4">
          {/* Left section: Logo and contact information */}
          <div className="flex items-center space-x-6">
            <a href="/" className="flex items-center space-x-2" aria-label="Home - Women and The Way">
              <img src={Logo} alt="Women and The Way Logo" className="h-8 w-auto" />
              <span className="text-xl md:text-2xl font-serif font-bold bg-gradient-to-r from-amber-500 to-amber-700 bg-clip-text text-transparent">
                Women and The Way
              </span>
            </a>
            {/* Contact information - hidden on mobile */}
            <div className="hidden lg:flex items-center space-x-4 text-sm">
              <a href="tel:0415537784" className="flex items-center text-sage-700 hover:text-amber-600 transition-colors" aria-label="Call us at 0415 537 784">
                <Phone className="h-4 w-4 mr-1" />
                <span className="sr-only">Call 0415 537 784</span>
              </a>
              <a href="mailto:womenandtheway@gmail.com" className="flex items-center text-sage-700 hover:text-amber-600 transition-colors" aria-label="Email us at womenandtheway@gmail.com">
                <Mail className="h-4 w-4 mr-1" />
                <span className="sr-only">Email womenandtheway@gmail.com</span>
              </a>
            </div>
          </div>

          {/* Right section: Navigation and mobile menu button */}
          <div className="flex items-center">
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1 desktop-navbar" role="navigation" aria-label="Main navigation">
              
              {/* Life Stages Dropdown */}
              <div className="relative">
                <button
                  onClick={toggleLifeStages}
                  className="px-3 py-2 text-sage-700 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors text-sm font-medium flex items-center gap-1"
                  aria-expanded={isLifeStagesOpen}
                  aria-haspopup="true"
                  aria-label="Life Stages menu"
                >
                  Life Stages
                  <ChevronDown className={cn("w-4 h-4 transition-transform", isLifeStagesOpen && "rotate-180")} aria-hidden="true" />
                </button>
                
                {isLifeStagesOpen && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-amber-200 rounded-md shadow-lg py-2 z-50" role="menu">
                    {lifeStagesItems.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className="block px-4 py-2 text-sage-700 hover:text-amber-600 hover:bg-amber-50 transition-colors text-sm"
                        onClick={() => setIsLifeStagesOpen(false)}
                        role="menuitem"
                      >
                        {item.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Support Services Dropdown */}
              <div className="relative">
                <button
                  onClick={toggleSupportServices}
                  className="px-3 py-2 text-sage-700 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors text-sm font-medium flex items-center gap-1"
                  aria-expanded={isSupportServicesOpen}
                  aria-haspopup="true"
                  aria-label="Support Services menu"
                >
                  Support Services
                  <ChevronDown className={cn("w-4 h-4 transition-transform", isSupportServicesOpen && "rotate-180")} aria-hidden="true" />
                </button>
                
                {isSupportServicesOpen && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-amber-200 rounded-md shadow-lg py-2 z-50" role="menu">
                    {supportServicesItems.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className="block px-4 py-2 text-sage-700 hover:text-amber-600 hover:bg-amber-50 transition-colors text-sm"
                        onClick={() => setIsSupportServicesOpen(false)}
                        role="menuitem"
                      >
                        {item.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Care Packages Dropdown */}
              <div className="relative">
                <button
                  onClick={toggleCarePackages}
                  className="px-3 py-2 text-sage-700 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors text-sm font-medium flex items-center gap-1"
                  aria-expanded={isCarePackagesOpen}
                  aria-haspopup="true"
                  aria-label="Care Packages menu"
                >
                  Care Packages
                  <ChevronDown className={cn("w-4 h-4 transition-transform", isCarePackagesOpen && "rotate-180")} aria-hidden="true" />
                </button>
                
                {isCarePackagesOpen && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-amber-200 rounded-md shadow-lg py-2 z-50" role="menu">
                    {carePackagesItems.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className="block px-4 py-2 text-sage-700 hover:text-amber-600 hover:bg-amber-50 transition-colors text-sm"
                        onClick={() => setIsCarePackagesOpen(false)}
                        role="menuitem"
                      >
                        {item.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Information Dropdown */}
              <div className="relative">
                <button
                  onClick={toggleInformation}
                  className="px-3 py-2 text-sage-700 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors text-sm font-medium flex items-center gap-1"
                  aria-expanded={isInformationOpen}
                  aria-haspopup="true"
                  aria-label="Information menu"
                >
                  Information
                  <ChevronDown className={cn("w-4 h-4 transition-transform", isInformationOpen && "rotate-180")} aria-hidden="true" />
                </button>
                
                {isInformationOpen && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-amber-200 rounded-md shadow-lg py-2 z-50" role="menu">
                    {informationItems.map((item) => (
                      <div key={item.name}>
                        {item.subItems ? (
                          <div className="relative group">
                            <div className="px-4 py-2 text-sage-700 text-sm font-medium flex items-center justify-between hover:bg-amber-50 hover:text-amber-600 transition-colors">
                              {item.name}
                              <ChevronDown className="w-3 h-3" aria-hidden="true" />
                            </div>
                            <div className="absolute top-full left-0 w-full bg-white border border-amber-200 rounded-md shadow-lg py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200" role="menu">
                              {item.subItems.map((subItem) => (
                                <a
                                  key={subItem.name}
                                  href={subItem.href}
                                  className="block px-4 py-2 text-sage-700 hover:text-amber-600 hover:bg-amber-50 transition-colors text-sm"
                                  onClick={() => setIsInformationOpen(false)}
                                  role="menuitem"
                                >
                                  {subItem.name}
                                </a>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <a
                            href={getNavHref(item.href)}
                            className="block px-4 py-2 text-sage-700 hover:text-amber-600 hover:bg-amber-50 transition-colors text-sm"
                            onClick={(e) => {
                              handleNavClick(e, item.href);
                              setIsInformationOpen(false);
                            }}
                            role="menuitem"
                          >
                            {item.name}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {menuItems.map((item) => (
                <a
                  key={item.name}
                  href={getNavHref(item.href)}
                  className="px-3 py-2 text-sage-700 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors text-sm font-medium"
                  onClick={(e) => handleNavClick(e, item.href)}
                >
                  {item.name}
                </a>
              ))}
              
              <Button
                asChild
                className="bg-amber-600 hover:bg-amber-700 text-white ml-2 px-4 py-2 rounded-md"
              >
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Book an appointment"
                >
                  Book Now
                </a>
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="lg:hidden p-2 text-sage-700 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors"
              aria-label="Toggle mobile menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {isMenuOpen && (
        <div 
          className="lg:hidden inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile menu */}
      <div
        id="mobile-menu"
        className={cn(
          "lg:hidden fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out",
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        )}
        role="navigation"
        aria-label="Mobile navigation"
      >
        {/* Mobile menu header */}
        <div className="flex items-center justify-between p-4 border-b border-amber-100">
          <div className="flex items-center space-x-2">
            <img src={Logo} alt="Women and The Way Logo" className="h-6 w-auto" />
            <span className="text-lg font-serif font-bold bg-gradient-to-r from-amber-500 to-amber-700 bg-clip-text text-transparent">
              Menu
            </span>
          </div>
          <button
            onClick={() => setIsMenuOpen(false)}
            className="p-2 text-sage-700 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Mobile menu content */}
        <div className="flex flex-col h-full overflow-y-auto">
          <nav className="flex-1 p-4 space-y-3">
            
            {/* Mobile Life Stages Section */}
            <div className="space-y-2">
              <button
                onClick={() => setMobileLifeStagesOpen(!mobileLifeStagesOpen)}
                className="w-full flex items-center justify-between py-4 px-4 text-sage-800 font-semibold text-left hover:bg-amber-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                aria-expanded={mobileLifeStagesOpen}
              >
                Life Stages
                <ChevronRight className={cn("w-5 h-5 transition-transform", mobileLifeStagesOpen && "rotate-90")} aria-hidden="true" />
              </button>
              <div className={cn(
                "overflow-hidden transition-all duration-300 ease-in-out",
                mobileLifeStagesOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
              )}>
                <div className="pl-4 space-y-2">
                  {lifeStagesItems.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="block py-3 px-4 text-sage-700 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Mobile Support Services Section */}
            <div className="space-y-2">
              <button
                onClick={() => setMobileSupportOpen(!mobileSupportOpen)}
                className="w-full flex items-center justify-between py-4 px-4 text-sage-800 font-semibold text-left hover:bg-amber-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                aria-expanded={mobileSupportOpen}
              >
                Support Services
                <ChevronRight className={cn("w-5 h-5 transition-transform", mobileSupportOpen && "rotate-90")} aria-hidden="true" />
              </button>
              <div className={cn(
                "overflow-hidden transition-all duration-300 ease-in-out",
                mobileSupportOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
              )}>
                <div className="pl-4 space-y-2">
                  {supportServicesItems.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="block py-3 px-4 text-sage-700 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Mobile Care Packages Section */}
            <div className="space-y-2">
              <button
                onClick={() => setMobileCarePackagesOpen(!mobileCarePackagesOpen)}
                className="w-full flex items-center justify-between py-4 px-4 text-sage-800 font-semibold text-left hover:bg-amber-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                aria-expanded={mobileCarePackagesOpen}
              >
                Care Packages
                <ChevronRight className={cn("w-5 h-5 transition-transform", mobileCarePackagesOpen && "rotate-90")} aria-hidden="true" />
              </button>
              <div className={cn(
                "overflow-hidden transition-all duration-300 ease-in-out",
                mobileCarePackagesOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
              )}>
                <div className="pl-4 space-y-2">
                  {carePackagesItems.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="block py-3 px-4 text-sage-700 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Mobile Information Section */}
            <div className="space-y-2">
              <button
                onClick={() => setMobileInfoOpen(!mobileInfoOpen)}
                className="w-full flex items-center justify-between py-4 px-4 text-sage-800 font-semibold text-left hover:bg-amber-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                aria-expanded={mobileInfoOpen}
              >
                Information
                <ChevronRight className={cn("w-5 h-5 transition-transform", mobileInfoOpen && "rotate-90")} aria-hidden="true" />
              </button>
              <div className={cn(
                "overflow-hidden transition-all duration-300 ease-in-out",
                mobileInfoOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
              )}>
                <div className="pl-4 space-y-2">
                  {informationItems.map((item) => (
                    <div key={item.name}>
                      {item.subItems ? (
                        <div className="space-y-2">
                          <div className="py-3 px-4 text-sage-700 text-sm font-medium bg-sage-50 rounded-md">
                            {item.name}
                          </div>
                          <div className="pl-4 space-y-2">
                            {item.subItems.map((subItem) => (
                              <a
                                key={subItem.name}
                                href={subItem.href}
                                className="block py-3 px-4 text-sage-700 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                                onClick={() => setIsMenuOpen(false)}
                              >
                                {subItem.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <a
                          href={getNavHref(item.href)}
                          className="block py-3 px-4 text-sage-700 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                          onClick={(e) => {
                            handleNavClick(e, item.href);
                            setIsMenuOpen(false);
                          }}
                        >
                          {item.name}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Blog link */}
            {menuItems.map((item) => (
              <a
                key={item.name}
                href={getNavHref(item.href)}
                className="block py-4 px-4 text-sage-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                onClick={(e) => {
                  handleNavClick(e, item.href);
                  setIsMenuOpen(false);
                }}
              >
                {item.name}
              </a>
            ))}
          </nav>

          {/* Mobile menu footer */}
          <div className="p-4 border-t border-amber-100 space-y-4">
            <div className="space-y-3">
              <a href="tel:0415537784" className="flex items-center text-sage-700 hover:text-amber-600 p-3 rounded-lg hover:bg-amber-50 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50" aria-label="Call us at 0415 537 784">
                <Phone className="h-5 w-5 mr-3" aria-hidden="true" />
                <span className="text-sm font-medium">0415 537 784</span>
              </a>
              <a href="mailto:womenandtheway@gmail.com" className="flex items-center text-sage-700 hover:text-amber-600 p-3 rounded-lg hover:bg-amber-50 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50" aria-label="Email us at womenandtheway@gmail.com">
                <Mail className="h-5 w-5 mr-3" aria-hidden="true" />
                <span className="text-sm font-medium">womenandtheway@gmail.com</span>
              </a>
            </div>
            <Button
              asChild
              className="w-full bg-amber-600 hover:bg-amber-700 text-white py-4 font-medium focus:ring-2 focus:ring-amber-500/50"
            >
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Book an appointment"
                onClick={() => setIsMenuOpen(false)}
              >
                Book Now
              </a>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default NavBar;
